import {
  InboxOutlined,
  LoadingOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Alert, Button, Modal, Result, Spin, Steps, Table, Upload } from 'antd';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadCSVAction } from 'src/store/actions/appointment.action';
import { showToastSuccess } from 'src/utilities/toast';
import {
  getEncountersCSVColumnMappings,
  syncEncounters,
} from '../../store/actions/encounter';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
import moment from 'moment';
// import './EncountersCSVUploader.scss';

const loadXLSX = () => import('xlsx');

const { Step } = Steps;
const { Dragger } = Upload;

const schema = {
  columns: [
    { name: 'Created Date Time', key: 'created_date_time', data_type: 'date' },
    { name: 'Start Date Time', key: 'start_date_time', data_type: 'date' },
    { name: 'Start Date', key: 'start_date', data_type: 'date' },
    { name: 'Start Time', key: 'start_time', data_type: 'time' },
    { name: 'Provider', key: 'provider', data_type: 'string' },
    {
      name: 'Patient First Name',
      key: 'patient_first_name',
      data_type: 'string',
    },
    {
      name: 'Patient Last Name',
      key: 'patient_last_name',
      data_type: 'string',
    },
    {
      name: 'Patient EHR/MRN ID',
      key: 'patient_ehr_id',
      data_type: 'string',
    },
    { name: 'Email', key: 'email', data_type: 'string' },
    { name: 'Phone Number', key: 'phone_number', data_type: 'string' },
    {
      name: 'Encounter Status',
      key: 'encounter_status',
      data_type: 'string',
    },
    { name: 'Birth Date', key: 'birth_date', data_type: 'date' },
    { name: 'Gender', key: 'gender', data_type: 'string' },
  ],
};

function formatTime(time) {
  if (!time) return '';

  const timeString = String(time);

  // Extract AM/PM indicator
  const period = timeString.match(/[APap][Mm]/)
    ? timeString.match(/[APap][Mm]/)[0].toUpperCase()
    : '';

  // Remove any non-digit characters, keeping the colon if present
  const cleanTime = timeString.replace(/[^\d:]/g, '');

  let formattedTime = '';

  if (cleanTime.includes(':')) {
    // If the time already contains a colon, just use it as is
    formattedTime = cleanTime;
  } else if (cleanTime.length === 3 || cleanTime.length === 4) {
    const hours = cleanTime.slice(0, -2);
    const minutes = cleanTime.slice(-2);
    formattedTime = `${hours}:${minutes}`;
  } else {
    // If the format is unexpected, return the original string
    return timeString;
  }

  // Append the period (AM/PM) if it exists
  return period ? `${formattedTime} ${period}` : formattedTime;
}

export const EncountersCSVUploader = ({ visible, setVisible }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [csvData, setCSVData] = useState([]);
  const [file, setFile] = useState(null);
  const [_uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, _setUploadResult] = useState(null);
  const [csvUploadedToS3, setCsvUploadedToS3] = useState(true);
  const [missingColumns, setMissingColumns] = useState([]);
  const { user } = useAuth();
  const [missingColumnsError, setMissingColumnsError] = useState(false);
  const dispatch = useDispatch();
  const {
    encountersCSVColumnMappings,
    encountersCSVColumnMappingsLoading,
    encountersCSVDateTimeFormats,
  } = useSelector((state) => state.encounterState);

  // Fetch CSV column mappings when the modal is opened
  useEffect(() => {
    if (visible) {
      dispatch(getEncountersCSVColumnMappings());
    }
  }, [visible]);

  const handleClose = () => {
    setCurrentStep(0);
    setCSVData([]);
    setUploadProgress(0);
    setVisible(false);
    setMissingColumns([]);
    setMissingColumnsError(false);
  };

  const getRecords = (jsonData) => {
    let encounters = [];
    let rowNumber = 2; // Start row number from 2 to avoid header row
    for (const record of jsonData) {
      encounters.push({
        row_number: rowNumber,
        encounter: {
          status: record.encounter_status || '',
          created_date_time: record.created_date_time || '',
          start_time: record.start_date_time
            ? record.start_date_time
            : record.start_date + ' ' + formatTime(record.start_time) || '',
        },
        provider: {
          name: record.provider ? String(record.provider).trim() : '',
        },
        patient: {
          birth_date: record.birth_date || '',
          first_name: record.patient_first_name
            ? String(record.patient_first_name).trim()
            : '',
          last_name: record.patient_last_name
            ? String(record.patient_last_name).trim()
            : '',
          gender: record.gender || '',
          email: record.email ? String(record.email).trim() : '',
          ehr_id: record.patient_ehr_id
            ? String(record.patient_ehr_id).trim()
            : '',
          phone: record.phone_number ? String(record.phone_number).trim() : '',
        },
      });
      rowNumber++;
    }
    return encounters;
  };

  const handleCSVUpload = async (file) => {
    if (encountersCSVColumnMappingsLoading) {
      return false;
    }

    try {
      let schemaCopy = JSON.parse(JSON.stringify(schema));

      if (Object.keys(encountersCSVColumnMappings).length != 0) {
        for (const column of schemaCopy.columns) {
          if (
            encountersCSVColumnMappings[column.name] &&
            encountersCSVColumnMappings[column.name] !== ''
          ) {
            column.name = encountersCSVColumnMappings[column.name];
          }
        }
      }

      const fileType = file.type === 'text/csv' ? 'CSV' : 'Excel';

      analytics.track(
        `Selected A ${fileType} File For Bulk Upload Encounters`,
        {
          email: user?.email,
        }
      );
      setFile(file); // Store the file for later use

      let jsonData = [];
      if (file.type === 'text/csv') {
        const csvContent = await file.text(); // Read the file content as text

        // Parse CSV to JSON
        const parsedData = Papa.parse(csvContent, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: 'greedy',
          transformHeader: (header) => header.trim(),
          transform: (value) => {
            if (typeof value === 'string') {
              return value.replace(/\r\n/g, '\n');
            }
            return value;
          },
        });

        // Map parsed data to schema dynamically
        jsonData = parsedData.data.map((row) => {
          const rowJson = {};
          schemaCopy.columns.forEach((column) => {
            rowJson[column.key] = row[column.name];
          });
          return rowJson;
        });
      } else if (
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel'
      ) {
        const XLSX = await loadXLSX();
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        const is1904 = workbook.Workbook?.WBProps?.date1904;

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const parsedData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: true,
        });

        // Helper function to convert format strings
        const convertDateFormat = (format) => {
          if (!format) return 'MM/DD/YYYY';
          return format
            .replace(/%m/g, 'MM')
            .replace(/%d/g, 'DD')
            .replace(/%Y/g, 'YYYY')
            .replace(/%y/g, 'YY')
            .replace(/%H/g, 'HH') // 24-hour format (00-23)
            .replace(/%I/g, 'hh') // 12-hour format with leading zero (01-12)
            .replace(/%M/g, 'mm')
            .replace(/%S/g, 'ss')
            .replace(/%p/g, 'A') // AM/PM
            .replace(/%-H/g, 'H') // 24-hour format without leading zero (0-23)
            .replace(/%-I/g, 'h') // 12-hour format without leading zero (1-12)
            .replace(/%-m/g, 'M') // month without leading zero (1-12)
            .replace(/%-d/g, 'D'); // day without leading zero (1-31)
        };

        function excelDateTimeToFullDateTime(serial) {
          if (typeof serial !== 'number') return serial;

          // Split into date and time parts
          const datePart = Math.floor(serial);
          const timePart = serial - datePart;

          // Convert date part
          const epochYear = is1904 ? 1904 : 1900;
          const epochAdjustment = is1904 ? 0 : 2;
          const excelEpoch = new Date(epochYear, 0, 1);
          const days = datePart - epochAdjustment;
          const date = new Date(
            excelEpoch.getTime() + days * 24 * 60 * 60 * 1000
          );

          // Only add time part if timePart is not 0
          if (timePart === 0) {
            return moment(date).format(
              convertDateFormat(encountersCSVDateTimeFormats.date_format)
            );
          }

          // Convert time part
          const totalMilliseconds = Math.round(timePart * 24 * 60 * 60 * 1000);

          const timeDate = new Date(totalMilliseconds);

          const hours = timeDate.getUTCHours();
          const minutes = timeDate.getUTCMinutes();
          const seconds = timeDate.getUTCSeconds();

          const timeMoment = moment()
            .hour(hours)
            .minute(minutes)
            .second(seconds);

          return (
            moment(date).format(
              convertDateFormat(encountersCSVDateTimeFormats?.date_format)
            ) +
            ' ' +
            timeMoment.format(
              convertDateFormat(encountersCSVDateTimeFormats?.time_format)
            )
          );
        }

        function excelDateToFullDate(serial) {
          if (typeof serial !== 'number') return serial;
          const epochYear = is1904 ? 1904 : 1900;
          const epochAdjustment = is1904 ? 0 : 2;

          const excelEpoch = new Date(epochYear, 0, 1);
          const days = serial - epochAdjustment;
          const date = new Date(
            excelEpoch.getTime() + days * 24 * 60 * 60 * 1000
          );

          return moment(date).format(
            convertDateFormat(encountersCSVDateTimeFormats?.date_format)
          );
        }

        function excelTimeToFullTime(serial) {
          if (typeof serial !== 'number') return serial;

          const totalMilliseconds = Math.round(serial * 24 * 60 * 60 * 1000);

          const timeDate = new Date(totalMilliseconds);

          const hours = timeDate.getUTCHours();
          const minutes = timeDate.getUTCMinutes();
          const seconds = timeDate.getUTCSeconds();

          const timeMoment = moment()
            .hour(hours)
            .minute(minutes)
            .second(seconds);

          return timeMoment.format(
            convertDateFormat(encountersCSVDateTimeFormats?.time_format)
          );
        }

        const headers = parsedData[0].map((header) => header.trim());

        const dateTimeColumns = ['start_date_time', 'created_date_time'];
        const timeColumns = ['start_time'];
        const dateColumns = ['start_date', 'birth_date'];

        const convertedData = parsedData
          .slice(1)
          .filter((row) => row.length > 0);

        jsonData = convertedData.map((row) => {
          const rowJson = {};
          schemaCopy.columns.forEach((column) => {
            const headerIndex = headers.findIndex(
              (header) => header === column.name
            );
            if (headerIndex !== -1) {
              let value = row[headerIndex] ?? null;

              // Convert based on column type or name patterns
              if (value !== null && typeof value === 'number') {
                if (dateColumns.includes(column.key) && value > 1000) {
                  value = excelDateToFullDate(value);
                } else if (timeColumns.includes(column.key) && value < 1) {
                  value = excelTimeToFullTime(value);
                } else if (
                  dateTimeColumns.includes(column.key) &&
                  value > 1000
                ) {
                  value = excelDateTimeToFullDateTime(value);
                }
              }

              rowJson[column.key] = value;
            }
          });
          return rowJson;
        });
      }

      const requiredColumns = ['provider', 'encounter_status', 'birth_date'];

      const firstRow = jsonData[0];

      // Check for date/time and patient columns
      const dateTimeColumns = firstRow['start_date_time']
        ? ['start_date_time']
        : ['start_date', 'start_time'];
      const patientColumns = ['patient_first_name', 'patient_last_name'];

      const allRequiredColumns = [
        ...requiredColumns,
        ...dateTimeColumns,
        ...patientColumns,
      ];

      const missingColumns = allRequiredColumns.filter(
        (key) => firstRow[key] === undefined
      );

      if (missingColumns.length > 0) {
        const missingColumnNames = missingColumns.reduce((acc, key) => {
          const specialCases = {
            start_date_time: 'Start Date Time or Start Date & Start Time',
            start_date: 'Start Date Time or Start Date & Start Time',
            start_time: 'Start Date Time or Start Date & Start Time',
          };

          const columnName =
            specialCases[key] ||
            schemaCopy.columns.find((column) => column.key === key)?.name;
          return columnName && !acc.includes(columnName)
            ? [...acc, columnName]
            : acc;
        }, []);

        setMissingColumns(missingColumnNames);
        setMissingColumnsError(true);
      }

      setCSVData(jsonData);

      handleNext();
    } catch (error) {
      console.error('Error reading file:', error);
    }
    return false; // Prevent upload
  };

  const generateColumns = () => {
    if (!csvData || csvData.length === 0) {
      return [];
    }
    const firstRow = csvData[0];
    const columnsToHideIfNotPresent = [
      'start_date_time',
      'start_date',
      'start_time',
    ];

    return schema.columns
      .map((column) => {
        if (
          columnsToHideIfNotPresent.includes(column.key) &&
          firstRow[column.key] === undefined
        ) {
          return null;
        }
        return {
          title: column.name === 'Sex' ? 'Gender' : column.name,
          dataIndex: column.key,
          key: column.key,
        };
      })
      .filter(Boolean); // Remove null entries
  };
  const handleNext = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const resetProgress = () => {
    setUploadProgress(0);
    setCurrentStep(1);
    analytics.track(
      'Failed To Calculate Time Needed To Upload CSV File For Bulk Upload Encounters',
      { email: user?.email }
    );
  };

  const uploadToS3 = async (file, key) => {
    showToastSuccess('Uploading file...');

    const base64File = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Extract base64 part
      reader.onerror = reject;
      reader.readAsDataURL(file); // [<mediatype>][;base64],<data>
    });

    const params = {
      s3_key: key,
      body: base64File,
      content_type: file.type,
    };

    await dispatch(uploadCSVAction(params));
    setCsvUploadedToS3(true);
  };

  const handleSubmit = async () => {
    analytics.track('Submitted CSV File For Bulk Upload Encounters', {
      email: user?.email,
    });
    setCsvUploadedToS3(false);
    const encounters = getRecords(csvData);

    const date = new Date();
    const s3_key =
      `${date.getUTCFullYear()}/${('0' + (date.getUTCMonth() + 1)).slice(
        -2
      )}/${('0' + date.getUTCDate()).slice(-2)}/` +
      `${date.getTime()}` +
      `.${file.name.split('.').pop()}`;

    const syncEncountersPayload = {
      s3_key: s3_key,
      encounters: encounters,
    };

    // Make the actual upload call.
    dispatch(syncEncounters(syncEncountersPayload)).then(async (result) => {
      if (result) {
        analytics.track(
          'Successfully Uploaded CSV File For Bulk Upload Encounters',
          { email: user?.email }
        );
        await uploadToS3(file, s3_key);
        setCurrentStep(2);
      } else {
        resetProgress();
        setCsvUploadedToS3(true);
      }
    });
  };

  const steps = [
    {
      title: 'Upload file',
      content: (
        <>
          <div className="flex-container items-center justify-center">
            <div className="w-3/4 items-center justify-center pt-12">
              {encountersCSVColumnMappingsLoading ? (
                <div className="flex flex-col items-center justify-center h-[250px]">
                  <Spin indicator={<LoadingOutlined spin />} size="large" />
                  <p className="text-xs mt-4">Loading column mappings...</p>
                </div>
              ) : (
                <Dragger
                  accept=".csv,.xlsx,.xls"
                  beforeUpload={handleCSVUpload}
                  height={250}
                  disabled={encountersCSVColumnMappingsLoading}
                >
                  <p className="ant-upload-drag-icon text-xs">
                    <InboxOutlined />
                  </p>
                  <p className="text-xs ant-upload-text">
                    Click or drag CSV, XLSX, or XLS file to this area to upload
                  </p>
                </Dragger>
              )}
            </div>
          </div>
        </>
      ),
    },
    {
      title: 'Preview changes',
      content: (
        <>
          {missingColumnsError ? (
            <div className="flex justify-center items-center h-64 mt-12">
              <Alert
                message="Uploaded file is missing the following required column(s)"
                description={
                  <>
                    <div>
                      <ul
                        style={{ listStyleType: 'disc', paddingLeft: '20px' }}
                      >
                        {missingColumns.map((column, index) => (
                          <li key={index}>{column}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-4 font-medium">
                      <p>
                        Please contact{' '}
                        <a href="mailto:help@insighthealth.ai">
                          help@insighthealth.ai
                        </a>{' '}
                        for support.
                      </p>
                    </div>
                  </>
                }
                type="error"
                showIcon
              />
            </div>
          ) : (
            <>
              <Table
                dataSource={csvData}
                columns={generateColumns()}
                pagination={false}
              />
              <div className="text-xs">Total Rows: {csvData.length}</div>
            </>
          )}
        </>
      ),
    },
    {
      title: 'Result',
      content: (
        <>
          <Result
            status="success"
            title="Upload Complete"
            subTitle="An email summary will be sent to you shortly."
          />
          {uploadResult?.errors && (
            <div className="flex flex-col items-center">
              <Table
                dataSource={uploadResult.errors.map((error, index) => ({
                  key: index,
                  reason: error,
                }))}
                pagination={false}
              >
                <Table.Column
                  title="Reason for skipping"
                  dataIndex="reason"
                  key="reason"
                  className="text-xs"
                />
              </Table>
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width="85vw"
      className="csv-uploader text-xs"
    >
      <Steps current={currentStep}>
        {steps.map((step) => (
          <Step key={step.title} title={step.title} />
        ))}
      </Steps>
      <>
        <div className="steps-action flex justify-right mb-2">
          {currentStep === 1 &&
            !missingColumnsError &&
            (csvUploadedToS3 ? (
              <Button
                type="primary"
                onClick={handleSubmit}
                icon={<SaveOutlined />}
              >
                Submit
              </Button>
            ) : (
              <Spin indicator={<LoadingOutlined spin />} size="large" />
            ))}
        </div>
        <div className="steps-content">{steps[currentStep].content}</div>
      </>
    </Modal>
  );
};

export default EncountersCSVUploader;
